import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AppWindow,
  ArrowLeft,
  Building2,
  Globe,
  KeyRound,
  Link2,
  MapPin,
  Plus,
  Store,
  Users,
} from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { ApplicationsTab } from "@/components/ApplicationsTab";
import { BrandEditor } from "@/components/BrandEditor";
import { M2MClientsTab } from "@/components/M2MClientsTab";
import { WebhookEndpointsTab } from "@/components/WebhookEndpointsTab";
import { ProvidersTab } from "@/components/ProvidersTab";
import { ScopedCredentialsPanel } from "@/components/ScopedCredentialsPanel";
import { ScopedPeopleTab } from "@/components/ScopedPeopleTab";
import { TechnicalDetails } from "@/components/TechnicalDetails";
import { usePartnerScope } from "@/components/partner-scope-context";
import { Toggle } from "@/components/Toggle";
import { usePageTitle } from "@/lib/use-page-title";
import { apiClient } from "@/lib/api";
import { describe } from "@/lib/api-error";
import { canCreateSubPartnerUnder, hasWriteOn, isSuperAdmin } from "@/lib/roles";
import { useRoleProfile } from "@/lib/use-role-profile";

import { cn } from "@/lib/utils";
import type { Partner } from "@tilt/admin-sdk";

import { CreateDialog as CreatePartnerDialog } from "./Partners";

type TabKey =
  | "overview"
  | "sub-partners"
  | "organizations"
  | "applications"
  | "providers"
  | "credentials"
  | "integrations"
  | "users";

const TABS: { key: TabKey; label: string; icon: typeof Building2 }[] = [
  { key: "overview", label: "Overview", icon: Building2 },
  { key: "sub-partners", label: "Sub-partners", icon: Building2 },
  { key: "organizations", label: "Organizations", icon: Store },
  { key: "applications", label: "Applications", icon: AppWindow },
  { key: "providers", label: "Providers", icon: Globe },
  { key: "credentials", label: "Credentials", icon: KeyRound },
  { key: "integrations", label: "Integrations", icon: Link2 },
  { key: "users", label: "People", icon: Users },
];

/**
 * Partner detail - per the locked tenancy model (2026-04-21), a
 * partner owns sub-partners, organizations, and users. This page is
 * the drill-down target from /partners.
 *
 * Handler lookups run against /admin/v1/partners (renamed from
 * /admin/v1/tenants in the V0018 cleanup) plus /organizations and
 * /users.
 */
export function PartnerDetailPage(): JSX.Element {
  usePageTitle("Partner");
  const { id = "" } = useParams<{ id: string }>();
  const { scope } = usePartnerScope();
  const [tab, setTab] = useState<TabKey>("overview");
  const profile = useRoleProfile();

  // Include scope.id in the query key so impersonation / scope-switch
  // doesn't hand back a cached row from the prior scope within
  // staleTime.
  const partner = useQuery({
    queryKey: ["partner", scope.id, id],
    queryFn: () => apiClient.getPartner(id),
    enabled: !!id,
    retry: false,
  });

  if (partner.isError) {
    return (
      <div className="space-y-4">
        <Link
          to="/partners"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Back to partners
        </Link>
        <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
          {partner.error instanceof Error ? partner.error.message : "Failed to load partner"}
        </div>
      </div>
    );
  }

  if (!partner.data) {
    return <p className="text-sm text-muted-foreground">Loading…</p>;
  }

  const p = partner.data;

  // Sub-partners tab is only meaningful when this partner either
  // already has sub-partners beneath it OR is allowed to spawn
  // them (``can_manage_sub_partners=true``). For non-super callers
  // viewing a partner with the flag off, the tab would only ever
  // render an empty list + no actionable controls - hide it.
  // Super-admins always see it because they're the ones who flip
  // the flag and need to manage the resulting tree.
  const showSubPartnersTab = p.can_manage_sub_partners || isSuperAdmin(profile);
  const visibleTabs = TABS.filter((t) => t.key !== "sub-partners" || showSubPartnersTab);
  // If the user had the Sub-partners tab active and the flag just
  // flipped off (or they navigated to a flag=false partner), pop
  // them back to Overview so we don't render a hidden tab's body.
  if (tab === "sub-partners" && !showSubPartnersTab) {
    setTab("overview");
  }

  return (
    <div className="space-y-6">
      <div>
        <Link
          to="/partners"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Back to partners
        </Link>
        <h1 className="mt-2 text-xl font-semibold tracking-tight sm:text-2xl">{p.name}</h1>
        <p className="break-words text-sm text-muted-foreground">
          <span className="font-mono">{p.path}</span>
        </p>
      </div>

      <nav aria-label="Partner sections" className="flex flex-wrap gap-1 border-b">
        {visibleTabs.map((t) => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={cn(
                "flex items-center gap-2 border-b-2 px-3 py-2 text-sm",
                active
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground",
              )}
            >
              <Icon className="h-4 w-4" />
              {t.label}
            </button>
          );
        })}
      </nav>

      {tab === "overview" && <OverviewTab partner={p} scopeId={scope.id} />}
      {tab === "sub-partners" && showSubPartnersTab && (
        <SubPartnersTab
          parentId={id}
          parentPath={p.path}
          parentCanManageSubPartners={p.can_manage_sub_partners}
          scopeId={scope.id}
        />
      )}
      {tab === "organizations" && <OrganizationsTab parentId={id} scopeId={scope.id} />}
      {tab === "applications" && (
        <ApplicationsTab scope="partner" id={p.id} readOnly={!hasWriteOn(profile, p.path)} />
      )}
      {tab === "providers" && (
        <ProvidersTab scope={{ kind: "partner", id: p.id, name: p.name, partnerPath: p.path }} />
      )}
      {tab === "credentials" && <ScopedCredentialsPanel partnerId={p.id} partnerName={p.name} />}
      {tab === "integrations" && (
        <div className="space-y-8">
          <M2MClientsTab
            partnerId={p.id}
            partnerPath={p.path}
            canWrite={hasWriteOn(profile, p.path)}
          />
          <hr className="border-border" />
          <WebhookEndpointsTab partnerId={p.id} canWrite={hasWriteOn(profile, p.path)} />
        </div>
      )}
      {tab === "users" && (
        <ScopedPeopleTab
          presetScope={{
            scope_kind: "partner",
            scope_id: p.id,
            partner_id: p.id,
            partner_path: p.path,
            label: p.name,
          }}
        />
      )}
    </div>
  );
}

function OverviewTab(props: { partner: Partner; scopeId: string }): JSX.Element {
  const queryClient = useQueryClient();
  // Brand + default-vertical edits are gated by
  // ``require_write(partner.path)`` on the server - partner_admins
  // can change those on their own partner. The ``can_manage_sub_partners``
  // flag is stricter: only Platform admins (super_admin) can flip it,
  // because letting a partner_admin grant themselves sub-partner
  // creation rights is the whole thing we're trying to prevent.
  // Backend enforces both rules; UI mirrors them so read-only or
  // non-super admins don't see controls that would 403 on submit.
  const profile = useRoleProfile();
  const canWrite = hasWriteOn(profile, props.partner.path);
  const canFlipSubPartnerFlag = isSuperAdmin(profile);
  // V0015 toggle - hide the edit toggle entirely once sub-partners
  // exist (per product spec). We still surface the read-only state
  // so the operator can see what's in effect; only the interactive
  // switch is gated. Query the sub-partner list the same way
  // SubPartnersTab does so the two tabs stay in sync.
  const subs = useQuery({
    queryKey: ["partners", props.scopeId, { parent_id: props.partner.id }],
    queryFn: () => apiClient.listPartners({ parent_id: props.partner.id }),
    retry: false,
  });
  const activeSubPartnerCount = (subs.data?.partners ?? []).filter(
    (t) => !t.is_deleted && t.partner_type === "partner",
  ).length;
  // Only flip to "locked (children exist)" messaging once the query
  // has successfully resolved with a non-empty result. `editable` is
  // only true when we know the count is 0; a loading/error state
  // renders its own branch instead of the misleading "locked" copy.
  const editable = subs.isSuccess && activeSubPartnerCount === 0;

  const toggle = useMutation({
    mutationFn: (value: boolean) =>
      apiClient.patchPartner(props.partner.id, { can_manage_sub_partners: value }),
    onSuccess: () => {
      // Invalidate both the single-partner fetch (for the detail
      // page to re-render with the new flag) and the sub-partner
      // list queries (harmless refresh, keeps things tidy).
      void queryClient.invalidateQueries({ queryKey: ["partner"] });
      void queryClient.invalidateQueries({ queryKey: ["partners"] });
    },
  });

  return (
    <div className="space-y-4">
      <div className="rounded-lg border bg-card p-4">
        <h2 className="mb-2 text-sm font-semibold">Summary</h2>
        <p className="text-sm text-muted-foreground">
          Use the tabs above to drill into sub-partners, organizations, and users rooted at{" "}
          <span className="font-mono">{props.partner.path}</span>.
        </p>
      </div>

      <DefaultVerticalCard partner={props.partner} canWrite={canWrite} />

      <DefaultCountryCodeCard partner={props.partner} canWrite={canWrite} />

      <ReceiptPrintEnabledCard partner={props.partner} canWrite={canWrite} />

      <AiPrewarmCard partner={props.partner} canWrite={isSuperAdmin(profile)} />

      <BrandEditor
        scope={{ kind: "partner", id: props.partner.id }}
        title="Partner branding"
        value={props.partner.brand_config ?? {}}
        disabled={!canWrite}
        onSave={async (next) => {
          await apiClient.patchPartner(props.partner.id, { brand_config: next });
          void queryClient.invalidateQueries({ queryKey: ["partner"] });
          void queryClient.invalidateQueries({ queryKey: ["partners"] });
        }}
      />

      {/* Sub-partner management card - only rendered when the toggle
          is actually editable (no sub-partners exist yet). Once
          sub-partners are in place the setting is effectively
          immutable, so we hide the whole card rather than showing a
          locked/disabled explanation that adds no actionable info. */}
      {editable && canFlipSubPartnerFlag && (
        <div className="rounded-lg border bg-card p-4">
          <h2 className="mb-1 text-sm font-semibold">Sub-partner management</h2>
          <p className="mb-2 text-[11px] text-muted-foreground">
            Platform-admin only - partner admins can't grant themselves sub-partner creation rights.
          </p>
          <div className="mt-2">
            <Toggle
              checked={props.partner.can_manage_sub_partners}
              disabled={toggle.isPending}
              onChange={(next) => toggle.mutate(next)}
              label="Allow this partner to manage sub-partners"
              description="When on, this partner's admins can create sub-partners nested under them. Turn off to restrict them to organizations + locations only."
            />
          </div>
          {toggle.isError && (
            <p className="mt-2 text-xs text-destructive">{describe(toggle.error)}</p>
          )}
        </div>
      )}

      {/* V0036 Platform-only flags - shown only to super_admin. These
          two are the escape hatches that let Platform ops delegate
          powers downward per partner: cascade the sub-partner flag,
          and allow direct-child impersonation. Partner admins never
          see these toggles (the backend also 403s the PATCH). */}
      {canFlipSubPartnerFlag && <PlatformOnlyFlagsCard partner={props.partner} />}

      <TechnicalDetails>
        <dl className="grid grid-cols-[auto,1fr] gap-x-3 gap-y-1 text-xs">
          <dt className="text-muted-foreground">Partner ID</dt>
          <dd className="font-mono">{props.partner.id}</dd>
          <dt className="text-muted-foreground">Path</dt>
          <dd className="font-mono">{props.partner.path}</dd>
        </dl>
      </TechnicalDetails>
    </div>
  );
}

/**
 * V0036 - Platform-only escape hatches. Only rendered when the
 * caller is super_admin (parent caller gates the render). Each toggle
 * writes a separate field on the partner row; both 403 on the server
 * for non-super callers, so UI gating is just hygiene.
 */
function PlatformOnlyFlagsCard(props: { partner: Partner }): JSX.Element {
  const queryClient = useQueryClient();
  const cascade = useMutation({
    mutationFn: (value: boolean) =>
      apiClient.patchPartner(props.partner.id, { can_cascade_sub_partner_flag: value }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["partner"] });
      void queryClient.invalidateQueries({ queryKey: ["partners"] });
    },
  });
  const impersonate = useMutation({
    mutationFn: (value: boolean) =>
      apiClient.patchPartner(props.partner.id, { can_impersonate: value }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["partner"] });
      void queryClient.invalidateQueries({ queryKey: ["partners"] });
    },
  });
  return (
    <div className="rounded-lg border bg-card p-4">
      <h2 className="mb-1 text-sm font-semibold">Platform-only controls</h2>
      <p className="mb-3 text-[11px] text-muted-foreground">
        These flags delegate sensitive powers to this partner's admins. Only Platform ops
        (super_admin) see or change them.
      </p>
      <div className="space-y-3">
        <Toggle
          checked={props.partner.can_cascade_sub_partner_flag}
          disabled={cascade.isPending}
          onChange={(next) => cascade.mutate(next)}
          label="Allow cascading sub-partner creation"
          description="When on, a partner_admin on this partner can grant can_manage_sub_partners=true on the sub-partners they create. Off by default."
        />
        {cascade.isError && <p className="text-xs text-destructive">{describe(cascade.error)}</p>}
        <Toggle
          checked={props.partner.can_impersonate}
          disabled={impersonate.isPending}
          onChange={(next) => impersonate.mutate(next)}
          label="Allow partner-admin impersonation of direct-child users"
          description="When on, partner_admins here can impersonate users whose primary_partner is a DIRECT child of this partner. Off by default."
        />
        {impersonate.isError && (
          <p className="text-xs text-destructive">{describe(impersonate.error)}</p>
        )}
      </div>
    </div>
  );
}

/**
 * V0022 - default vertical picker on the partner detail. Writes
 * partner.default_vertical_id. Cascades as the org create-dialog's
 * suggested default.
 */
function DefaultVerticalCard(props: { partner: Partner; canWrite: boolean }): JSX.Element {
  const queryClient = useQueryClient();
  const verticalsQuery = useQuery({
    queryKey: ["verticals"],
    queryFn: () => apiClient.listVerticals(),
    staleTime: 60_000,
  });
  const verticals = verticalsQuery.data?.verticals ?? [];
  const current = props.partner.default_vertical_id ?? "";

  const save = useMutation({
    mutationFn: (value: string) =>
      apiClient.patchPartner(props.partner.id, {
        default_vertical_id: value || null,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["partner"] });
      void queryClient.invalidateQueries({ queryKey: ["partners"] });
    },
  });

  return (
    <div className="rounded-lg border bg-card p-4">
      <h2 className="mb-1 text-sm font-semibold">Default vertical</h2>
      <p className="mb-3 text-xs text-muted-foreground">
        A preset that cascades to every organization created under this partner. Orgs can still add
        other verticals and pick a different default - this is just the initial suggestion.
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <select
          value={current}
          onChange={(e) => save.mutate(e.target.value)}
          disabled={verticalsQuery.isLoading || save.isPending || !props.canWrite}
          title={!props.canWrite ? "You don't have write access on this partner" : undefined}
          className="rounded-md border bg-background px-3 py-2 text-sm"
        >
          <option value="">(none - orgs pick their own)</option>
          {verticals.map((v) => (
            <option key={v.id} value={v.id}>
              {v.display_name}
            </option>
          ))}
        </select>
        {save.isPending && <span className="text-xs text-muted-foreground">Saving…</span>}
        {save.isError && (
          <span className="text-xs text-destructive">
            {save.error instanceof Error ? save.error.message : "Save failed"}
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * V0081 - partner-level default country code. Stored in
 * ``partner_tbl.partner_config.default_country_code`` and consumed
 * by tilt-pos-api when normalising customer phones for SMS receipts:
 * if a customer phone doesn't already start with ``+``, the cashier's
 * digits get this prefix before Twilio dispatch. Each org/location
 * inherits from its parent partner; locations rarely need their own
 * value so we expose this only at the partner tier.
 */
// Common locales we'll likely add Twilio numbers for. Free-form string
// in the underlying column, so partners with an exotic prefix can still
// edit the input directly. The list mirrors common cashier intuitions
// - North America first, then UK + India + LatAm + EMEA.
const COUNTRY_CODE_PRESETS = [
  { code: "+1", label: "+1 - US / Canada" },
  { code: "+44", label: "+44 - United Kingdom" },
  { code: "+91", label: "+91 - India" },
  { code: "+52", label: "+52 - Mexico" },
  { code: "+61", label: "+61 - Australia" },
  { code: "+33", label: "+33 - France" },
  { code: "+49", label: "+49 - Germany" },
  { code: "+81", label: "+81 - Japan" },
];

const COUNTRY_CODE_CUSTOM_SENTINEL = "__custom__";

function DefaultCountryCodeCard(props: { partner: Partner; canWrite: boolean }): JSX.Element {
  const queryClient = useQueryClient();
  const current = props.partner.partner_config?.default_country_code ?? "+1";
  const [draft, setDraft] = useState<string>(current);

  // Re-sync the draft whenever the partner row changes (navigating
  // between detail routes without unmount, or a refetch normalised
  // the value). Without this, a user opening Partner A → Partner B
  // would still see Partner A's draft and a Save click would PATCH
  // the wrong partner.
  useEffect(() => {
    setDraft(current);
  }, [current, props.partner.id]);

  const save = useMutation({
    mutationFn: (value: string) =>
      apiClient.patchPartner(props.partner.id, {
        partner_config: { default_country_code: value },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["partner"] });
      void queryClient.invalidateQueries({ queryKey: ["partners"] });
    },
  });

  // Tightened to match the tilt-admin-api regex (rejects +0 / +00 etc).
  const valid = /^\+[1-9]\d{0,2}$/.test(draft);
  const dirty = draft !== current;
  const isPreset = COUNTRY_CODE_PRESETS.some((p) => p.code === draft);

  return (
    <div className="rounded-lg border bg-card p-4">
      <h2 className="mb-1 text-sm font-semibold">Default country code</h2>
      <p className="mb-3 text-xs text-muted-foreground">
        E.164 prefix (e.g. <span className="font-mono">+1</span>) prepended to bare customer phones
        before SMS receipts go out. Used when the cashier types a 10-digit number without a country
        code.
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <select
          // The "(custom)" sentinel switches the control into free-
          // form mode by clearing the draft so the text input takes
          // focus. Picking a preset overrides the draft. Bind via a
          // sentinel value so the option is actually selectable -
          // the prior shape used "" + a fallback that snapped right
          // back to the previous preset (Copilot, PR #159).
          value={isPreset ? draft : COUNTRY_CODE_CUSTOM_SENTINEL}
          onChange={(e) => {
            const v = e.target.value;
            if (v === COUNTRY_CODE_CUSTOM_SENTINEL) {
              setDraft("");
            } else {
              setDraft(v);
            }
          }}
          disabled={save.isPending || !props.canWrite}
          title={!props.canWrite ? "You don't have write access on this partner" : undefined}
          className="rounded-md border bg-background px-3 py-2 text-sm"
        >
          <option value={COUNTRY_CODE_CUSTOM_SENTINEL}>(custom)</option>
          {COUNTRY_CODE_PRESETS.map((p) => (
            <option key={p.code} value={p.code}>
              {p.label}
            </option>
          ))}
        </select>
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value.trim())}
          maxLength={4}
          disabled={save.isPending || !props.canWrite}
          placeholder="+1"
          className="w-20 rounded-md border bg-background px-3 py-2 font-mono text-sm"
          aria-label="Country code"
        />
        <button
          type="button"
          onClick={() => save.mutate(draft)}
          disabled={!props.canWrite || !valid || !dirty || save.isPending}
          className="rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {save.isPending ? "Saving…" : "Save"}
        </button>
        {!valid && draft !== "" && (
          <span className="text-xs text-destructive">
            Use the E.164 form: <span className="font-mono">+</span> followed by 1–3 digits, leading
            digit non-zero.
          </span>
        )}
        {save.isError && (
          <span className="text-xs text-destructive">
            {save.error instanceof Error ? save.error.message : "Save failed"}
          </span>
        )}
      </div>
    </div>
  );
}

/** V0082 - toggle to enable/disable the print-receipt UI in tilt-pos for
 *  all locations under this partner. Defaults to enabled (null / unset).
 *  The cascade resolver ANDs partner → org → location values, so disabling
 *  at the partner level suppresses print everywhere regardless of lower
 *  settings. */
function ReceiptPrintEnabledCard(props: { partner: Partner; canWrite: boolean }): JSX.Element {
  const queryClient = useQueryClient();
  const current = props.partner.partner_config?.receipt_print_enabled ?? true;
  const [draft, setDraft] = useState<boolean>(current);

  useEffect(() => {
    setDraft(props.partner.partner_config?.receipt_print_enabled ?? true);
  }, [props.partner.id, props.partner.partner_config?.receipt_print_enabled]);

  const save = useMutation({
    mutationFn: (value: boolean) =>
      apiClient.patchPartner(props.partner.id, {
        partner_config: { receipt_print_enabled: value },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["partner"] });
      void queryClient.invalidateQueries({ queryKey: ["partners"] });
    },
  });

  const dirty = draft !== current;

  return (
    <div className="rounded-lg border bg-card p-4">
      <h2 className="mb-1 text-sm font-semibold">Print receipts</h2>
      <p className="mb-3 text-xs text-muted-foreground">
        When disabled, the Print button is hidden in tilt-pos for all locations under this partner.
        Individual orgs and locations can also disable print, but cannot re-enable it if the partner
        has disabled it.
      </p>
      <div className="flex flex-wrap items-center gap-3">
        <label className="flex cursor-pointer items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={draft}
            onChange={(e) => setDraft(e.target.checked)}
            disabled={save.isPending || !props.canWrite}
            className="h-4 w-4 rounded border accent-primary"
          />
          Allow print
        </label>
        <button
          type="button"
          onClick={() => save.mutate(draft)}
          disabled={!props.canWrite || !dirty || save.isPending}
          className="rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {save.isPending ? "Saving…" : "Save"}
        </button>
        {save.isError && (
          <span className="text-xs text-destructive">
            {save.error instanceof Error ? save.error.message : "Save failed"}
          </span>
        )}
      </div>
    </div>
  );
}

/** V0111 - toggle to enable background AI recommendation pre-warming for
 *  this partner. Off by default. When on, the pre-warmer Lambda runs
 *  every 30 minutes and generates insights before cashiers open their
 *  dashboards. Each cycle costs LLM credits proportional to the number
 *  of active locations. */
function AiPrewarmCard(props: { partner: Partner; canWrite: boolean }): JSX.Element {
  const queryClient = useQueryClient();
  const current = props.partner.partner_config?.ai_prewarm_enabled ?? false;
  const [draft, setDraft] = useState<boolean>(current);

  useEffect(() => {
    setDraft(props.partner.partner_config?.ai_prewarm_enabled ?? false);
  }, [props.partner.id, props.partner.partner_config?.ai_prewarm_enabled]);

  const save = useMutation({
    mutationFn: (value: boolean) =>
      apiClient.patchPartner(props.partner.id, {
        partner_config: { ai_prewarm_enabled: value },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["partner"] });
      void queryClient.invalidateQueries({ queryKey: ["partners"] });
    },
  });

  const dirty = draft !== current;

  return (
    <div className="rounded-lg border bg-card p-4">
      <h2 className="mb-1 text-sm font-semibold">AI insight pre-warming</h2>
      <p className="mb-3 text-xs text-muted-foreground">
        When on, the system generates AI dashboard insights in the background every 30 minutes so
        cashiers see them instantly on login. Off by default. Each cycle costs LLM credits for every
        active location under this partner.
      </p>
      <div className="flex flex-wrap items-center gap-3">
        <label className="flex cursor-pointer items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={draft}
            onChange={(e) => setDraft(e.target.checked)}
            disabled={save.isPending || !props.canWrite}
            className="h-4 w-4 rounded border accent-primary"
          />
          Enable pre-warming
        </label>
        <button
          type="button"
          onClick={() => save.mutate(draft)}
          disabled={!props.canWrite || !dirty || save.isPending}
          className="rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {save.isPending ? "Saving..." : "Save"}
        </button>
        {save.isError && (
          <span className="text-xs text-destructive">
            {save.error instanceof Error ? save.error.message : "Save failed"}
          </span>
        )}
      </div>
    </div>
  );
}

function SubPartnersTab(props: {
  parentId: string;
  parentPath: string;
  parentCanManageSubPartners: boolean;
  scopeId: string;
}): JSX.Element {
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const list = useQuery({
    queryKey: ["partners", props.scopeId, { parent_id: props.parentId }],
    queryFn: () => apiClient.listPartners({ parent_id: props.parentId }),
    retry: false,
  });

  const subs = list.data?.partners ?? [];

  // Mirror the partners.py rule: super-admin bypasses; anyone else
  // needs ``can_manage_sub_partners`` flipped on for THIS parent AND
  // a write-capable grant whose path covers the parent's path.
  // Passing ``parentPath`` so the helper can run the ltree-cover
  // check against the caller's role grants - matches
  // ``caller.require_write`` on the server.
  const profile = useRoleProfile();
  const canCreate = canCreateSubPartnerUnder(profile, {
    can_manage_sub_partners: props.parentCanManageSubPartners,
    path: props.parentPath,
  });

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold">Sub-partners</h2>
        {canCreate && (
          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:opacity-90"
          >
            <Plus className="h-4 w-4" /> New sub-partner
          </button>
        )}
      </div>

      {createOpen && canCreate && (
        <CreatePartnerDialog
          defaultParentId={props.parentId}
          onClose={() => setCreateOpen(false)}
          onMutated={() => {
            setCreateOpen(false);
            void queryClient.invalidateQueries({ queryKey: ["partners"] });
          }}
        />
      )}
      {list.isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
      {subs.length === 0 && list.isSuccess && (
        <div className="rounded-lg border border-dashed bg-muted/40 p-6 text-center text-sm text-muted-foreground">
          No sub-partners under <span className="font-mono">{props.parentPath}</span>. Create one to
          nest a reseller or affiliate under this partner.
        </div>
      )}
      {subs.length > 0 && (
        <ul className="divide-y rounded-lg border bg-card">
          {subs.map((s) => (
            <li key={s.id}>
              <Link
                to={`/partners/${s.id}`}
                className="flex items-center justify-between px-4 py-3 text-sm hover:bg-muted/40"
              >
                <span>
                  <span className="font-medium">{s.name}</span>{" "}
                  <span className="font-mono text-xs text-muted-foreground">{s.path}</span>
                </span>
                <span className="text-xs text-muted-foreground">View →</span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function OrganizationsTab(props: { parentId: string; scopeId: string }): JSX.Element {
  const list = useQuery({
    queryKey: ["organizations", props.scopeId, { partner_id: props.parentId }],
    queryFn: () => apiClient.listOrganizations({ partner_id: props.parentId }),
    retry: false,
  });

  const orgs = list.data?.organizations ?? [];

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold">Organizations</h2>
        <Link
          to={`/organizations?partner=${props.parentId}&create=1`}
          className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:opacity-90"
        >
          <Plus className="h-4 w-4" /> New organization
        </Link>
      </div>
      {list.isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
      {orgs.length === 0 && list.isSuccess && (
        <div className="rounded-lg border border-dashed bg-muted/40 p-6 text-center text-sm text-muted-foreground">
          No organizations under this partner yet. Organizations are the customer accounts you
          onboard under a partner umbrella.
        </div>
      )}
      {orgs.length > 0 && (
        <ul className="divide-y rounded-lg border bg-card">
          {orgs.map((o) => (
            <li key={o.id} className="px-4 py-3 text-sm">
              <div className="flex items-center justify-between gap-3">
                <Link
                  to={`/organizations/${o.id}`}
                  className="min-w-0 truncate font-medium hover:underline"
                >
                  {o.name}
                </Link>
                <div className="flex flex-shrink-0 items-center gap-1.5">
                  <Link
                    to={`/locations?organization_id=${o.id}`}
                    className="inline-flex items-center gap-1 rounded-md border bg-background px-2 py-1 text-xs text-muted-foreground hover:text-foreground"
                    title="View this organization's locations"
                  >
                    <MapPin className="h-3 w-3" /> Locations
                  </Link>
                  <Link
                    to={`/organizations/${o.id}`}
                    className="inline-flex items-center gap-1 rounded-md border bg-background px-2 py-1 text-xs text-muted-foreground hover:text-foreground"
                    title="Open organization details"
                  >
                    <Building2 className="h-3 w-3" /> Open
                  </Link>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
